// Turns portal records (clients, quotations, rentals + meter readings) into the shapes tallyXml.js needs.
// Pure functions - no React/Firebase - so they can be tested on their own.

import { round2, voucherMessage, stateFromGstin, gstTotals } from './tallyXml';

const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

export const findClientByName = (clients, name) => clients.find(c => norm(c.name) === norm(name));

// ------------------------------------------------------------ clients -> ledgers

export const clientToLedger = (c, cfg) => ({
  name: String(c.name || '').trim(),
  parent: cfg.debtorsGroup,
  address: c.address || '',
  gstin: c.gstin || '',
  state: stateFromGstin(c.gstin) || '',
  phone: c.phone || '',
  email: c.email || ''
});

// ------------------------------------------------------------ quotations -> Sales Order

// Which income ledger a quotation's lines post to
const LEDGER_FOR_QUOTATION_TYPE = {
  service: 'service', email_service: 'service',
  rental: 'rental', laptop_rental: 'rental',
  sale: 'sales', cartridge: 'sales', server_sale: 'sales', license_sale: 'sales'
};

export const quotationToVoucher = (q, client, cfg) => {
  const key = LEDGER_FOR_QUOTATION_TYPE[q.quotationType || 'service'] || 'service';
  const ledger = cfg.ledgers[key];
  const lines = (q.items || []).map(i => ({
    ledger,
    amount: round2((parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0)),
    description: i.description
  })).filter(l => l.amount > 0);
  return {
    date: q.date,
    voucherType: cfg.vouchers.order,
    party: (client && client.name) || q.clientName,
    partyGstin: (client && client.gstin) || q.clientGSTIN || '',
    reference: q.quoteNumber,
    narration: `Quotation ${q.quoteNumber}${q.printerModel ? ' - ' + q.printerModel : ''}`,
    lines
  };
};

// ------------------------------------------------------------ rentals + meter readings -> Sales vouchers

const monthRange = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { start: `${ym}-01`, end: `${ym}-${String(last).padStart(2, '0')}`, label: new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }), yymm: `${String(y).slice(2)}${String(m).padStart(2, '0')}` };
};
export const monthInfo = monthRange;

// Extra-page charge for one meter reading - same maths as Reports > Meter Readings
export const billableForReading = (r, rental) => {
  if (!rental || rental.rentalType === 'standby') return 0;
  const perPage = rental.rentalType === 'per_page';
  const used = (cur, prev) => (r[cur] || 0) - (r[prev] || 0);
  const bill = (u, free) => (perPage ? u : Math.max(0, u - (free || 0)));
  return round2(
    bill(used('a4Reading', 'previousA4'), rental.a4FreePages) * (rental.a4PageCost || 0) +
    bill(used('a3Reading', 'previousA3'), rental.a3FreePages) * (rental.a3PageCost || 0) +
    bill(used('colourA4Reading', 'previousColourA4'), rental.colourA4FreePages) * (rental.colourA4PageCost || 0) +
    bill(used('colourA3Reading', 'previousColourA3'), rental.colourA3FreePages) * (rental.colourA3PageCost || 0)
  );
};

const shortHash = (s) => {
  let h = 0; for (const ch of String(s)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h.toString(36).toUpperCase().padStart(5, "0").slice(-5);
};

/**
 * One billing row per client for the month.
 * Returns [{ clientName, client, lines, skipped: [text], voucher (or null), totals, reference, key }]
 */
export const computeBilling = ({ rentals, meterReadings, clients, month, companyCode, companyId, cfg }) => {
  const range = monthRange(month);
  const byClient = new Map();
  const bucket = (name) => {
    const k = norm(name);
    if (!byClient.has(k)) byClient.set(k, { clientName: name, lines: [], skipped: [] });
    return byClient.get(k);
  };

  rentals.forEach(r => {
    if (r.status === 'returned') return;
    if (r.startDate && r.startDate > range.end) return;           // not started yet
    const b = bucket(r.clientName || 'Unknown');
    const what = `${r.assetNumber || r.serialNumber || ''} ${r.printerModel || ''}`.trim();
    if (r.rentalType === 'standby') { b.skipped.push(`${what}: standby rental is billed per cartridge - add manually`); return; }
    if (r.rentalType !== 'per_page' && (r.monthlyRate || 0) > 0) {
      b.lines.push({ ledger: cfg.ledgers.rental, amount: round2(r.monthlyRate), description: `Rent ${what} - ${range.label}` });
    }
    const readings = meterReadings.filter(m => m.serialNumber === r.serialNumber && m.date && m.date >= range.start && m.date <= range.end);
    const extra = round2(readings.reduce((s, m) => s + billableForReading(m, r), 0));
    if (extra > 0) b.lines.push({ ledger: cfg.ledgers.rental, amount: extra, description: `Page charges ${what} - ${range.label}` });
  });

  return [...byClient.values()].filter(b => b.lines.length > 0 || b.skipped.length > 0).map(b => {
    const client = findClientByName(clients, b.clientName);
    const reference = `${companyCode}R${range.yymm}-${shortHash(client ? client.id : b.clientName)}`;
    const voucher = b.lines.length && client ? {
      date: range.end,
      voucherType: cfg.vouchers.sales,
      party: client.name,
      partyGstin: client.gstin || '',
      reference,
      narration: `Rental and usage charges - ${range.label}`,
      lines: b.lines
    } : null;
    const totals = voucher ? voucherMessage(voucher, cfg).totals : { net: round2(b.lines.reduce((s, l) => s + l.amount, 0)), cgst: 0, sgst: 0, igst: 0, total: 0 };
    return {
      key: `bill:${companyId}:${month}:${client ? client.id : norm(b.clientName)}`,
      clientName: b.clientName, client, lines: b.lines, skipped: b.skipped, voucher, totals, reference,
      problem: !client ? 'Client not found in Clients list' : (!b.lines.length ? 'Nothing billable this month' : '')
    };
  }).sort((a, b) => a.clientName.localeCompare(b.clientName));
};


// ------------------------------------------------------------ invoices -> Sales voucher

export const INVOICE_LEDGER_KINDS = [
  { value: 'sales', label: 'Sale of goods / licences' },
  { value: 'rental', label: 'Rental' },
  { value: 'service', label: 'Service' }
];

export const invoiceTotals = (inv, ownState) => {
  const net = round2((inv.items || []).reduce((s, i) => s + (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0), 0));
  const partyState = stateFromGstin(inv.clientGSTIN) || ownState;
  return gstTotals(net, inv.gstRate, partyState, ownState);
};

export const invoiceToVoucher = (inv, client, cfg) => {
  const ledger = String(inv.incomeLedger || '').trim() || cfg.ledgers[inv.ledgerKind || 'sales'] || cfg.ledgers.sales;   // a specific Tally income ledger, else the generic one for the type
  return {
    date: inv.date,
    voucherType: cfg.vouchers.sales,
    party: (client && client.name) || inv.clientName,
    partyGstin: inv.clientGSTIN || (client && client.gstin) || '',
    reference: inv.invoiceNumber,
    narration: `Invoice ${inv.invoiceNumber}${inv.notes ? ' - ' + String(inv.notes).slice(0, 80) : ''}`,
    gstRate: inv.gstRate === undefined || inv.gstRate === '' ? 18 : Number(inv.gstRate),
    lines: (inv.items || []).map(i => ({ ledger, amount: round2((parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0)), description: i.description })).filter(l => l.amount > 0)
  };
};


// Proforma invoice -> non-posting Memorandum voucher (Tally has no proforma voucher). Same lines/GST as the invoice it would become.
export const proformaToVoucher = (p, client, cfg) => ({
  ...invoiceToVoucher(p, client, cfg),
  voucherType: cfg.vouchers.memo || 'Memorandum', memo: true,
  narration: `Proforma invoice ${p.invoiceNumber}${p.notes ? ' - ' + String(p.notes).slice(0, 80) : ''}`
});

// Delivery challan -> Memorandum voucher. A challan carries quantities only, so the value comes from the quotation it was
// fetched from (qty x rate of its lines) or from the optional "value for Tally" typed on the challan. No value -> nothing to post.
export const challanToVoucher = (ch, client, cfg, quotation) => {
  const party = (client && client.name) || ch.clientName;
  const base = { date: ch.date, voucherType: cfg.vouchers.memo || 'Memorandum', memo: true, party, partyGstin: ch.clientGSTIN || (client && client.gstin) || '', reference: ch.challanNumber };
  const items = (ch.items || []).map(i => `${i.description} x${i.qty}`).join(', ').slice(0, 150);
  const narration = `Delivery challan ${ch.challanNumber}${ch.quotationNumber ? ' (quotation ' + ch.quotationNumber + ')' : ''}: ${items}`;
  if (quotation) {
    const v = quotationToVoucher(quotation, client, cfg);
    if (v.lines.length) return { ...base, narration, gstRate: v.gstRate, lines: v.lines };
  }
  const val = round2(parseFloat(ch.declaredValue) || 0);
  const isRental = /rental/i.test(ch.purpose || '');
  const ledger = (isRental ? cfg.ledgers.rental : cfg.ledgers.sales) || cfg.ledgers.sales;
  return { ...base, narration, gstRate: 18, lines: val > 0 ? [{ ledger, amount: val, description: narration }] : [] };
};

// Suggest the Tally income ledger for a document from the real ledger names fetched from Tally (Sales Accounts).
// Tally books often have one ledger per category, e.g. "Printer Service @18%", "Rental Laptop 18%", "New Desktop Sales @ 18%".
export const suggestIncomeLedger = ({ quotationType, printerType }, names) => {
  const list = names || [];
  if (!list.length) return '';
  const cat = ['laserjet_printer', 'inktank_printer', 'barcode_printer'].includes(printerType) ? 'printer'
    : printerType === 'scanner' ? 'scanner' : printerType === 'laptop' ? 'laptop' : printerType === 'desktop' ? 'desktop'
    : ['toner_cartridge', 'toner_cartridge_parts'].includes(printerType) ? 'consumable' : '';
  const kind = ['rental', 'laptop_rental'].includes(quotationType) ? 'rental' : ['service', 'email_service'].includes(quotationType) ? 'service' : 'sale';
  const find = (...res) => { for (const re of res) { const hit = list.find(x => re.test(x)); if (hit) return hit; } return ''; };
  const rules = {
    'rental|printer': [/rental\s+printer/i], 'rental|scanner': [/rental\s+scanner/i], 'rental|laptop': [/rental\s+laptop/i], 'rental|desktop': [/rental\s+desktop/i],
    'service|printer': [/printer\s+service/i], 'service|scanner': [/scanner\s+service/i], 'service|laptop': [/laptop\s+service/i], 'service|desktop': [/desktop\s+service/i],
    'sale|printer': [/new\s+printer\s+sales/i], 'sale|scanner': [/new\s+scanner\s+sales/i], 'sale|laptop': [/new\s+laptop\s+sales/i], 'sale|desktop': [/new\s+desktop\s+sales/i],
    'sale|consumable': [/toner/i, /consumable/i]
  };
  const specific = find(...(rules[`${kind}|${cat}`] || []));
  if (specific) return specific;
  if (kind === 'rental') return find(/^rental\s+income$/i, /rental/i);
  if (kind === 'service') return find(/^service\s+income$/i, /service/i);
  return find(/^gst\s+sale$/i, /^sales$/i, /sale/i);
};
